import React, { useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import {
  CalendarItemTypeSelectFragment,
  useGetCalendarClassTypesSelectQuery,
} from "./CalendarItemTypeSelect-generated.hooks";
import { sortByName } from "../../../utils/sortUtils";

type CalendarItemTypeControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends CalendarItemTypeSelectFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<
  TFieldValues,
  TName,
  CalendarItemTypeSelectFragment,
  IsMulti,
  Group
>;

function CalendarItemTypeController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends CalendarItemTypeSelectFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: CalendarItemTypeControllerProps<
    TFieldValues,
    TName,
    CalendarItemTypeSelectFragment,
    IsMulti,
    Group
  > &
    (ReactSelectSyncProps | ReactSelectAsyncProps) & {
      initialCalendarTypeUuid?: string;
    },
) {
  const { ...other } = props;

  const { data } = useGetCalendarClassTypesSelectQuery({
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const getOptionLabel = (option: CalendarItemTypeSelectFragment) =>
    option.name;

  const getOptionColor = (option: CalendarItemTypeSelectFragment) =>
    option.color;

  const getOptionValue = (option: CalendarItemTypeSelectFragment) =>
    String(option.id);

  const options = useMemo(() => {
    const calendarTypes = data?.getCalendarClassTypes || [];

    return calendarTypes
      .filter((calendarType) => {
        if (calendarType.uuid === props.initialCalendarTypeUuid) {
          return true;
        }

        return calendarType.isActive && !calendarType.isDeleted;
      })
      .sort(sortByName);
  }, [data, props.initialCalendarTypeUuid]);

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...other}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      getOptionColor={getOptionColor}
      enableOptionColor
      enableSingleValueColor
    />
  );
}

export default CalendarItemTypeController;
